import React, { useEffect, useState } from "react";
import { Download, X, Share2 } from "lucide-react";
import { useI18n } from "../i18n";

const DISMISSED_KEY = "goldora_pwa_dismiss_at";
const DISMISS_DAYS = 7;
const DELAY_MS = 30000; // 30s on site before showing

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith("android-app://")
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

function isMobile() {
  return isIOS() || isAndroid();
}

function dismissedRecently() {
  try {
    const at = localStorage.getItem(DISMISSED_KEY);
    if (!at) return false;
    const ms = Date.now() - parseInt(at, 10);
    return ms < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function PwaInstallPrompt() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosCard, setIosCard] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;

    // Android / desktop Chrome — capture beforeinstallprompt
    const beforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const t = setTimeout(() => setShow(true), DELAY_MS);
      return () => clearTimeout(t);
    };
    window.addEventListener("beforeinstallprompt", beforeInstall);

    // iOS — no event, manually time the banner
    let iosTimer;
    if (isIOS() && !isStandalone()) {
      iosTimer = setTimeout(() => setShow(true), DELAY_MS);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch {/* noop */}
    setShow(false);
    setIosCard(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        dismiss();
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setIosCard(true);
    }
  };

  if (!show) return null;
  if (!isMobile() && !deferredPrompt) return null;

  return (
    <>
      <div
        className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[70] rise-in"
        data-testid="pwa-install-banner"
      >
        <div className="relative overflow-hidden rounded-xl border border-gold-500/35 bg-black/95 backdrop-blur-md shadow-[0_8px_30px_rgb(0,0,0,0.6)]">
          <div
            aria-hidden
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(circle, #E9B934 0%, transparent 60%)" }}
          />
          <div className="relative p-4 flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg overflow-hidden border border-gold-500/30 bg-black grid place-items-center shrink-0">
              <img src="/goldora-icon-trans.png" alt="GOLDORA" className="w-9 h-9 object-contain" draggable="false" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gold-400">
                {pt ? "Instalar app" : "Install app"}
              </div>
              <div className="font-display text-base text-white mt-0.5 leading-tight">
                {pt ? "Tenha GOLDORA na sua tela inicial" : "GOLDORA on your home screen"}
              </div>
              <div className="text-xs text-neutral-400 mt-1 leading-snug">
                {pt
                  ? "1 toque, sem App Store. Acesso direto, modo tela cheia."
                  : "1 tap, no App Store. Direct access, full screen."}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={install}
                  data-testid="pwa-install-btn"
                  className="inline-flex items-center gap-1.5 bg-gold-500 hover:bg-gold-400 text-black text-xs font-medium px-3 py-2 rounded-md transition-colors"
                >
                  <Download size={13} />
                  {pt ? "Instalar" : "Install"}
                </button>
                <button
                  onClick={dismiss}
                  data-testid="pwa-dismiss-btn"
                  className="text-xs text-neutral-500 hover:text-neutral-300 px-2 py-2"
                >
                  {pt ? "Agora não" : "Not now"}
                </button>
              </div>
            </div>
            <button
              onClick={dismiss}
              aria-label="Close"
              className="text-neutral-500 hover:text-white -mt-1 -mr-1"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      {iosCard && (
        <div
          className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm grid place-items-end sm:place-items-center p-4"
          onClick={() => setIosCard(false)}
          data-testid="pwa-ios-modal"
        >
          <div
            className="relative surface-elevated w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIosCard(false)}
              className="absolute top-3 right-3 text-neutral-500 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden border border-gold-500/30 bg-black grid place-items-center shrink-0">
                <img src="/goldora-icon-trans.png" alt="GOLDORA" className="w-10 h-10 object-contain" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400">
                  {pt ? "iOS · Como instalar" : "iOS · How to install"}
                </div>
                <h3 className="font-display text-xl text-white mt-0.5">
                  {pt ? "3 passos rápidos" : "3 quick steps"}
                </h3>
              </div>
            </div>

            <ol className="mt-6 space-y-3 text-sm text-neutral-200">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-gold-500/15 border border-gold-500/30 grid place-items-center text-gold-300 font-mono text-xs shrink-0">1</span>
                <span>
                  {pt
                    ? <>Toque no botão <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><Share2 size={11} />Compartilhar</span> na barra do Safari.</>
                    : <>Tap the <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><Share2 size={11} />Share</span> button in Safari.</>
                  }
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-gold-500/15 border border-gold-500/30 grid place-items-center text-gold-300 font-mono text-xs shrink-0">2</span>
                <span>
                  {pt
                    ? <>Role a lista e toque em <strong className="text-gold-300">Adicionar à Tela de Início</strong>.</>
                    : <>Scroll and tap <strong className="text-gold-300">Add to Home Screen</strong>.</>
                  }
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-gold-500/15 border border-gold-500/30 grid place-items-center text-gold-300 font-mono text-xs shrink-0">3</span>
                <span>
                  {pt
                    ? <>Toque em <strong className="text-gold-300">Adicionar</strong>. Pronto — ícone dourado na sua tela inicial.</>
                    : <>Tap <strong className="text-gold-300">Add</strong>. Done — gold icon on your home screen.</>
                  }
                </span>
              </li>
            </ol>

            <button
              onClick={dismiss}
              data-testid="pwa-ios-close-btn"
              className="mt-6 w-full bg-gold-500 hover:bg-gold-400 text-black font-medium py-2.5 rounded-md transition-colors"
            >
              {pt ? "Entendi" : "Got it"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
